// M4-A4 — Auth mobile Bearer per Capacitor. Additivo: sul web tutto no-op (cookie httpOnly invariati).
// Token SOLO in secure storage nativo (Keychain iOS / Keystore Android). MAI localStorage.
// Nessun token in URL, query, DOM o log.
import { Capacitor } from "@capacitor/core";
import { SecureStorage } from "@aparajita/capacitor-secure-storage";
import axios from "axios";

// Fallback produzione: la build mobile (Codemagic) NON ha i file .env (gitignored) né una
// var di shell → senza fallback REACT_APP_BACKEND_URL sarebbe undefined e ogni chiamata
// punterebbe a "undefined/api". Web e preview continuano a usare process.env (impostato).
const BACKEND = process.env.REACT_APP_BACKEND_URL || "https://tricomas.it";
const API = `${BACKEND}/api`;
const ACCESS_KEY = "mas_access_token";
const REFRESH_KEY = "mas_refresh_token";

export const isNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
};

// Import statico del plugin (API ufficiale: `SecureStorage` è il proxy registrato).
// Rimosso l'helper dinamico secureStore()/import() per eliminare qualsiasi await non
// necessario prima delle operazioni Keychain.
export async function getAccessToken() {
  if (!isNative()) return null;
  try {
    return await SecureStorage.get(ACCESS_KEY);
  } catch (e) {
    return null;
  }
}

async function getRefreshToken() {
  if (!isNative()) return null;
  try {
    return await SecureStorage.get(REFRESH_KEY);
  } catch (e) {
    return null;
  }
}

// Guard anti-hang: SecureStorage.set() su iOS può, in rari casi, non risolvere mai
// (proxy plugin non-thenable / Keychain). Limitiamo a 8s e propaghiamo un errore chiaro.
// Nessun token in localStorage, nessun fallback silenzioso: se la persistenza fallisce,
// il login DEVE fallire (l'errore risale a loginMobile → Auth.submit).
function ssTimeout(promise, ms = 8000) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("secure_storage_timeout")), ms)),
  ]);
}

async function setTokens(access, refresh) {
  try {
    console.info("[mas-auth] secure_access_write_start");
    await ssTimeout(SecureStorage.set(ACCESS_KEY, access));
    console.info("[mas-auth] secure_access_write_ok");
    if (refresh) {
      console.info("[mas-auth] secure_refresh_write_start");
      await ssTimeout(SecureStorage.set(REFRESH_KEY, refresh));
      console.info("[mas-auth] secure_refresh_write_ok");
    }
  } catch (e) {
    const kind = e?.message === "secure_storage_timeout" ? "timeout" : "error";
    console.warn(`[mas-auth] secure_${kind}`);
    throw new Error("secure_storage_error");
  }
}

export async function clearTokens() {
  if (!isNative()) return;
  try {
    await ssTimeout(SecureStorage.remove(ACCESS_KEY));
    await ssTimeout(SecureStorage.remove(REFRESH_KEY));
  } catch (e) {
    /* niente da pulire: non bloccante */
  }
}

// Login mobile: usa /auth/login/mobile e salva i token nel secure storage. Ritorna l'utente.
export async function loginMobile(email, password) {
  const { data } = await axios.post(`${API}/auth/login/mobile`, { email, password }, { timeout: 20000 });
  console.info("[mas-auth] login_http_ok");
  await setTokens(data.access_token, data.refresh_token);
  return data.user;
}

// Logout mobile: rimuove i token locali (nessun endpoint server-side: token_version resta globale).
export async function logoutMobile() {
  await clearTokens();
}

// Refresh mobile SINGLE-FLIGHT: una sola richiesta concorrente condivisa (nessun loop/race).
// Usa axios "nudo" (non l'istanza con interceptor) per evitare ricorsione.
let _refreshPromise = null;
async function _doRefresh() {
  const rt = await getRefreshToken();
  if (!rt) throw new Error("no_refresh_token");
  const { data } = await axios.post(`${API}/auth/refresh/mobile`, { refresh_token: rt }, { timeout: 20000 });
  await setTokens(data.access_token, data.refresh_token);
  return data.access_token;
}

export function refreshMobileTokens() {
  if (!_refreshPromise) {
    _refreshPromise = _doRefresh().finally(() => {
      _refreshPromise = null;
    });
  }
  return _refreshPromise;
}
